import type { ColumnDef } from '@tanstack/react-table';

import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { useDynamicLocale } from '@/hoslog-intl';
import { EyeIcon } from '@/icons';
import { formatDate } from '@/lib/dateFormatter';
import { formatAmount } from '@/lib/format';
import type { ParsedDateRange } from '@/lib/parseDateRange';
import { AffiliateData } from '@/services/payment/payment.types';
import { Link } from '@tanstack/react-router';
import { getMonthDateRangeValue, getRowYear } from '../utils/monthDateRange';

/** "Jan 2024" (en) / "2024年1月" (ja), matching the design. */
export const formatMonthAndYear = (monthNumber: number, year: number) =>
  formatDate(`${year}-${monthNumber}`, 'MMM YYYY');

export function getColumns(
  t: ReturnType<typeof useDynamicLocale>['t'],
  dateRange: ParsedDateRange,
): ColumnDef<AffiliateData>[] {
  return [
    {
      id: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="font-medium truncate max-w-[10rem]">{getSerialNumber(table, row.index)}</div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: 'monthAndYear',
      accessorKey: 'monthNumber',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.monthAndYear')} />
      ),
      cell: ({ row }) => (
        <div className="font-medium truncate max-w-[20rem]">
          {formatMonthAndYear(row.original.monthNumber, getRowYear(row.original, dateRange))}
        </div>
      ),
      meta: {
        label: t('common.page.tableColumn.monthAndYear'),
      },
      enableHiding: false,
    },
    {
      id: 'createdDate',
      accessorKey: 'withdrawDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.withdrawDay')} />
      ),
      cell: ({ row }) => <div>{row.original.withdrawDate}</div>,
      meta: {
        label: t('common.page.tableColumn.withdrawDay'),
      },
      enableHiding: false,
      enableColumnFilter: true,
    },
    {
      accessorKey: 'totalRequestedAmount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.totalRequestedAmount')} />
      ),
      cell: ({ row }) => <div>{formatAmount(row.original.totalRequestedAmount)}</div>,
      meta: {
        label: t('common.page.tableColumn.totalRequestedAmount'),
        align: 'right',
      },
      enableHiding: false,
    },
    {
      id: 'actions',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.action')} />,
      cell: ({ row }) => (
        <Link
          to="/payment-management/affiliate-log/$monthly-report"
          params={{ 'monthly-report': row.original.paymentId }}
          // The detail API has no payment id, so the detail page queries (and bounds its date filter to) this month.
          search={{
            month: getMonthDateRangeValue(row.original.monthNumber, getRowYear(row.original, dateRange)),
          }}
        >
          <EyeIcon className="stroke-warning size-4" />
        </Link>
      ),
      size: 40,
      enableSorting: false,
      enableHiding: false,
    },
  ];
}
